/* eslint-disable jira/restricted/@atlassian/react-sweet-state */
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import {
	type Action,
	createStore,
	createActionsHook,
	createStateHook,
} from '@atlassian/react-sweet-state';
import type { AccountId } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	setUserProperties,
	getUserProperty,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';

import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { COHORT3_CONSOLIDATION_BANNER, BANNER_HEIGHT } from '../../common/constants.tsx';

type State = {
	isDismissed: boolean;
	height: number;
	isLoading: boolean;
};

export const initialState: State = {
	isDismissed: false,
	height: BANNER_HEIGHT,
	isLoading: true,
};

export const actions = {
	onDismiss:
		(accountId: AccountId): Action<State> =>
		async ({ setState }) => {
			if (!accountId) {
				fireErrorAnalytics({
					meta: {
						id: 'onDismiss',
						packageName: 'jiraEocChangeboarding',
						teamName: 'alexstrasza',
					},
					error: new Error('Account id is not available'),
					sendToPrivacyUnsafeSplunk: true,
				});
				return;
			}

			try {
				await setUserProperties(accountId, COHORT3_CONSOLIDATION_BANNER, 'true');

				setState({
					isDismissed: true,
				});
			} catch (error) {
				setState({
					isDismissed: true,
				});

				fireErrorAnalytics({
					meta: {
						id: 'useCohort3CustomersBanner',
						packageName: 'Cohort3CustomerBanner',
						teamName: 'alexstrasza',
					},
					error: error instanceof Error ? error : new Error(String(error)),
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		},

	fetchBannerVisibility:
		(accountId: AccountId): Action<State> =>
		async ({ setState }) => {
			if (!accountId) {
				fireErrorAnalytics({
					meta: {
						id: 'fetchBannerVisibility',
						packageName: 'Cohort3CustomerBanner',
						teamName: 'alexstrasza',
					},
					error: new Error('Account id is not available'),
					sendToPrivacyUnsafeSplunk: true,
				});
				return;
			}

			try {
				const value = await getUserProperty<boolean>(accountId, COHORT3_CONSOLIDATION_BANNER);
				setState({
					isDismissed: Boolean(value),
					isLoading: false,
				});
			} catch (error) {
				if (error instanceof FetchError && (error.statusCode === 404 || error.statusCode === 403)) {
					setState({
						isLoading: false,
						isDismissed: false,
					});
				}
			}
		},
} as const;

type Actions = typeof actions;

const Store = createStore<State, Actions>({
	name: 'Cohort3CustomerBannerStore',
	initialState,
	actions,
});

export const useCohort3CustomerBannerState = createStateHook(Store);
export const useCohort3CustomerBannerActions = createActionsHook(Store);
